import type { FC } from 'react';
import React from 'react';

import { convertStructToJson } from './../../../../../ricos-content/lib/struct-converters.js';
import { Node_Type } from './../../../../../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { CustomClassNames } from './../../../../../ricos-types/index.js';

import type { FromExternalNode, FromExternalNodeProps } from '../../../types.js';
import { getAlignmentClassName, getSizeClassName, getTextWrapClassName } from './getClassNames.js';
import { nodeViewContainer as NodeViewContainer } from './nodeViewContainer.js';

const isDraftEntityConfig = (data: Record<string, any>): boolean =>
  (data.width || data.alignment || data.textWrap) &&
  (['content', 'original', 'fullWidth', 'small'].includes(data.size) ||
    ['center', 'left', 'right'].includes(data.alignment));

const toContainerData = (data: Record<string, any>): Record<string, any> => ({
  ...(typeof data.size === 'string' ? { width: { size: data.size.toUpperCase() } } : {}),
  ...(typeof data.alignment === 'string' ? { alignment: data.alignment.toUpperCase() } : {}),
  ...(typeof data.textWrap !== 'undefined' ? { textWrap: data.textWrap !== 'nowrap' } : {}),
});

const getContainerData = (externalData: Record<string, any>) => {
  if (isDraftEntityConfig(externalData.config || {})) {
    return toContainerData(externalData.config);
  }
  return externalData.containerData;
};

const Plugin: FC<FromExternalNodeProps> = ({
  type,
  context,
  node,
  nodeIndex,
  children,
  pluginComponent: { component: Component },
}) => {
  const settings = context.config?.[type] || {};
  const componentData = (node.externalData && convertStructToJson(node.externalData)) || {};
  const containerData = getContainerData(componentData) || {};
  const componentProps = {
    type,
    componentData,
    settings,
    children,
    ...context,
    blockKey: node.id,
  };

  const customClassNames: CustomClassNames = {
    alignment: getAlignmentClassName(containerData),
    size: getSizeClassName(containerData),
    textWrap: getTextWrapClassName(containerData),
  };

  return (
    <NodeViewContainer node={node} nodeIndex={nodeIndex} customClassNames={customClassNames}>
      <Component {...componentProps} />
    </NodeViewContainer>
  );
};

export const fromExternalNode: FromExternalNode = args => {
  return {
    [Node_Type.EXTERNAL]: ({ node, nodeIndex }) => {
      return <Plugin key={node.id} node={node} nodeIndex={nodeIndex} {...args} />;
    },
  };
};
